import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ItemService } from '../api/GlobalItemService';
import { GlobalItem } from '../api/protosCompiled/globalItem/globalItem_pb';
import { FolderService } from '../api/FolderService';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../stores/store';

export function useFetchItemsByFileId(fileId: string, options = {}) {
  return useQuery<GlobalItem.AsObject[], Error>(
    ['itemsByFileId', fileId], // fileId is synonymous with itemId
    async () => {
      if (!fileId) {
        return [];
      }
      const res = await ItemService.getItemsByFileId(fileId);
      return res.itemsList || [];
    },
    {
      enabled: !!fileId,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      // staleTime: 30000, // Consider data fresh for 30 seconds
      ...options,
    }
  );
}

export function useCreateOrUpdateItem() {
  const queryClient = useQueryClient();
  const { selectedEstimate } = useStore(useUnityBuildStore);
  return useMutation<void, Error, GlobalItem>(
    async (data: GlobalItem) => {
      await ItemService.createOrUpdateGlobalItem(data);
    },
    {
      onSuccess: (data, variables) => {
        // Get the parent ID from the variables
        const parentId = variables.getParentid();

        // Invalidate and force refetch for specific parent
        queryClient.invalidateQueries(['itemsByFileId', parentId]);
        queryClient.refetchQueries(['itemsByFileId', parentId], {
          exact: true,
        });

        // Check if this is an update with a different parent
        const itemId = variables.getItemid();
        if (itemId) {
          // If updating an existing item, need to also refresh any other
          // potentially affected folders
          queryClient.invalidateQueries('itemsByFileId');
        }

        // Also invalidate all item folders to update the folder tree
        queryClient.invalidateQueries('itemFolders');

        // Invalidate extension query if we have an estimate selected
        if (selectedEstimate?.estimateid) {
          queryClient.invalidateQueries(['extension', selectedEstimate.estimateid]);
        }
      },
      onError: err => {
        console.error('Mutation error:', err);
      },
    }
  );
}

export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(
    async (itemId: string) => {
      await FolderService.deleteFolder(itemId);
    },
    {
      onSuccess: (data, itemId) => {
        // Get the parent ID of the deleted item from the cache if possible
        const queries = queryClient.getQueryCache().findAll(['itemsByFileId']);

        // Invalidate all itemsByFileId queries to ensure proper refreshing
        queryClient.invalidateQueries('itemsByFileId');

        // Force refetch for all relevant queries
        queries.forEach(query => {
          queryClient.refetchQueries(query.queryKey, { exact: true });
        });

        // Also invalidate item folders
        queryClient.invalidateQueries('itemFolders');
      },
      onError: err => {
        console.error('Error deleting item:', err);
      },
    }
  );
}
